import { useEffect, useState } from 'react';
import {
  FolderBase, FolderSummary, createFolderApi, deleteFolderApi, folderDeletionApi,
  moveFormsApi, renameFolderApi,
} from '../../api/access';
import { FormSummary } from '../../api/forms';
import { Modal } from '../../components/fs';

/**
 * The four folder modals: new, rename, delete, and move.
 *
 * All `narrow` (520px), footer `primary · Cancel`, one open at a time — the
 * caller owns a single `editor` so opening one closes any other (rule 6).
 *
 * Every one of them states its consequence in ACCESS terms rather than folder
 * terms, because "moved to Field ops" is not the fact anybody needs; "six more
 * people can now see it" is.
 */

/** New folder — name plus the same two base-access radios as the share dialog,
 *  so the concept is learned once rather than twice. */
export function NewFolderModal({ onClose, onDone }: {
  onClose: () => void;
  onDone: (folder: FolderSummary) => void;
}) {
  const [name, setName] = useState('');
  const [base, setBase] = useState<FolderBase>('everyone');
  const [level, setLevel] = useState<'view' | 'edit'>('edit');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!name.trim() || busy) return;
    setBusy(true); setError('');
    try {
      onDone((await createFolderApi(name.trim(), base, level)).folder);
      onClose();
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <Modal
      title="New folder" narrow onClose={onClose}
      footer={<>
        <button className="fs-btn primary" onClick={save} disabled={!name.trim() || busy}>Create folder</button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>}
    >
      {error && <div className="ad-err" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="fs-field">
        <label htmlFor="fo-name">Folder name</label>
        <input id="fo-name" autoFocus value={name} maxLength={60}
          onChange={(e) => setName(e.target.value)} placeholder="Field ops" />
      </div>

      <div className="fs-field" style={{ marginTop: 16 }}><label>Who can reach the forms in it</label></div>
      <label className={`fs-choice${base === 'everyone' ? ' on' : ''}`}>
        <input type="radio" checked={base === 'everyone'} onChange={() => setBase('everyone')} />
        <span>
          <b>Everyone in the account</b>
          <small>Anyone who can sign in reaches the forms you put in here.</small>
        </span>
        {base === 'everyone' && (
          <select className="fs-input fs-choice-level"
            value={level} onChange={(e) => setLevel(e.target.value as 'view' | 'edit')}
            onClick={(e) => e.stopPropagation()}>
            <option value="edit">Edit</option>
            <option value="view">View</option>
          </select>
        )}
      </label>
      <label className={`fs-choice${base === 'restricted' ? ' on' : ''}`}>
        <input type="radio" checked={base === 'restricted'} onChange={() => setBase('restricted')} />
        <span>
          <b>Only the people I share it with</b>
          <small>Plus Owners and Admins, who always see every form. Share it once it exists.</small>
        </span>
      </label>
    </Modal>
  );
}

/** Rename. Save stays disabled until the name actually changes. */
export function RenameFolderModal({ folder, onClose, onDone }: {
  folder: FolderSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const [name, setName] = useState(folder.name);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const changed = name.trim() && name.trim() !== folder.name;

  async function save() {
    if (!changed || busy) return;
    setBusy(true); setError('');
    try { await renameFolderApi(folder.id, name.trim()); onDone(); onClose(); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <Modal
      title="Rename folder" narrow onClose={onClose}
      footer={<>
        <button className="fs-btn primary" onClick={save} disabled={!changed || busy}>Save</button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>}
    >
      {error && <div className="ad-err" style={{ marginBottom: 12 }}>{error}</div>}
      <div className="fs-field">
        <label htmlFor="fo-rename">Folder name</label>
        <input id="fo-rename" autoFocus value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      </div>
    </Modal>
  );
}

/**
 * Delete — a dialog, not a `window.confirm` (rule 5).
 *
 * The forms inside are never deleted; they fall back to Unfiled. The sentence
 * says so with the count, and names who loses the access the folder was
 * handing out, because that is the part nobody can see coming.
 */
export function DeleteFolderModal({ folder, onClose, onDone }: {
  folder: FolderSummary;
  onClose: () => void;
  onDone: () => void;
}) {
  const [info, setInfo] = useState<{ formCount: number; losing: { type: string; name: string }[] } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    folderDeletionApi(folder.id).then(setInfo).catch((e) => setError(e.message));
  }, [folder.id]);

  async function remove() {
    if (busy) return;
    setBusy(true); setError('');
    try { await deleteFolderApi(folder.id); onDone(); onClose(); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <Modal
      title={<>Delete the <b>{folder.name}</b> folder?</>} narrow onClose={onClose}
      footer={<>
        <button className="fs-btn danger" onClick={remove} disabled={busy}>Delete folder</button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>}
    >
      {error && <div className="ad-err" style={{ marginBottom: 12 }}>{error}</div>}
      {info && (
        <div className="fs-consequence">
          {info.formCount > 0 ? (
            <><b>The {info.formCount} {info.formCount === 1 ? 'form' : 'forms'} inside will move to Unfiled.</b>{' '}
              Nothing is deleted but the folder itself.</>
          ) : (
            <b>The folder is empty.</b>
          )}
          {!!info.losing.length && (
            <div style={{ marginTop: 8 }}>
              {info.losing.map((l) => l.name).join(', ')}{' '}
              {info.losing.length === 1 ? 'loses' : 'lose'} the access this folder was giving them.
              A form that said something about itself keeps saying it.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

/**
 * Move to… — one form from a row menu, or many from the bulk footer.
 *
 * Destinations you cannot move into stay VISIBLE and inert with the reason on
 * the row. A shortened list would make a folder look deleted, and "where did
 * Field ops go?" is a worse question than "why can't I pick it?".
 *
 * `Unfiled` is a destination here and never a folder in the rail's menu: it is
 * a state a form can be put into, not a place that can be managed.
 */
export function MoveFormsModal({ forms, folders, onClose, onDone }: {
  forms: FormSummary[];
  folders: FolderSummary[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [target, setTarget] = useState<number | null | undefined>(undefined);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const chosen = folders.find((f) => f.id === target);
  const many = forms.length > 1;

  async function move() {
    if (target === undefined || busy) return;
    setBusy(true); setError('');
    try { await moveFormsApi(forms.map((f) => f.id), target ?? null); onDone(); onClose(); }
    catch (e: any) { setError(e.message); } finally { setBusy(false); }
  }

  return (
    <Modal
      title={many ? `Move ${forms.length} forms` : <>Move <b>{forms[0]?.name}</b></>}
      narrow onClose={onClose}
      footer={<>
        <button className="fs-btn primary" onClick={move} disabled={target === undefined || busy}>
          {many ? `Move ${forms.length} forms` : 'Move form'}
        </button>
        <button className="fs-btn neutral" onClick={onClose}>Cancel</button>
      </>}
    >
      {error && <div className="ad-err" style={{ marginBottom: 12 }}>{error}</div>}

      {many && (
        <div className="fs-cell-sub" style={{ marginBottom: 12 }}>
          {forms.map((f) => f.name).join(', ')}
        </div>
      )}

      <label className={`fs-choice${target === null ? ' on' : ''}`}>
        <input type="radio" checked={target === null} onChange={() => setTarget(null)} />
        <span>
          <b>Unfiled</b>
          <small>Out of every folder. A form here is reachable by everyone unless it says otherwise.</small>
        </span>
      </label>

      {folders.map((f) => (
        <label key={f.id} className={`fs-choice${target === f.id ? ' on' : ''}`}>
          <input type="radio" checked={target === f.id} onChange={() => setTarget(f.id)} />
          <span>
            <b>{f.name}</b>
            <small>
              {f.form_count} {f.form_count === 1 ? 'form' : 'forms'} ·{' '}
              {f.base_access === 'everyone'
                ? `everyone in the account can ${f.base_level}`
                : 'restricted to the people it is shared with'}
            </small>
          </span>
        </label>
      ))}

      {/* The consequence, in access terms rather than folder terms. */}
      {target !== undefined && (
        <div className="fs-consequence" style={{ marginTop: 14 }}>
          {chosen
            ? (chosen.base_access === 'everyone'
              ? <>Once moved, {many ? 'these forms' : 'this form'} will be reachable by{' '}
                <b>everyone in the account</b>, at {chosen.base_level} — unless {many ? 'a form' : 'it'} sets
                its own access.</>
              : <>Once moved, {many ? 'these forms' : 'this form'} will be reachable only by the people{' '}
                <b>{chosen.name}</b> is shared with, plus Owners and Admins.</>)
            : <>Once unfiled, {many ? 'these forms' : 'this form'} will be reachable by{' '}
              <b>everyone in the account</b> — unless {many ? 'a form' : 'it'} sets its own access.</>}
        </div>
      )}
    </Modal>
  );
}
